import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { ApiOutlined, LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  bindTrigger,
  listFlowBindings,
  TRIGGER_KEY_PATTERN,
  unbindTrigger,
} from '@/api/flowBindings';
import type { FlowBindingKind, FlowBindingView, FlowBindingsView } from '@/api/types';
import { isNotAvailable, normalizeApiError, type NormalizedApiError } from '@/api/errors';
import { getPermissionMatrix } from '@/admin/permissionMatrix';

const { Text, Paragraph } = Typography;

export interface TriggersDrawerProps {
  open: boolean;
  flowId: string;
  flowName?: string;
  /** The version a new binding pins — bindings name the PUBLISHED artifact only. */
  publishedVersion: number | null;
  onClose: () => void;
}

const KIND_HELP: Record<FlowBindingKind, string> = {
  CLOCK: 'A lifecycle clock type falling DUE (periodic review, LEI renewal, retention hold…).',
  SIGNAL: 'An inbound signal type resolving to an entity (CKYCRR update notification…).',
  API: 'An operator/service-invoked name resolved by the API kind (e.g. ENTITY_EXIT).',
};

function rolesFor(key: string): string[] {
  try {
    return getPermissionMatrix().keys.find((k) => k.key === key)?.roles ?? [];
  } catch {
    return [];
  }
}

function ts(v?: string | null): string {
  return v ? new Date(v).toLocaleString() : '—';
}

function KindTag({ kind }: { kind: FlowBindingKind }) {
  const color = kind === 'CLOCK' ? 'gold' : kind === 'SIGNAL' ? 'geekblue' : 'purple';
  return (
    <Tag color={color} bordered={false} style={{ fontSize: 10.5 }}>
      {kind}
    </Tag>
  );
}

/**
 * The per-flow "Triggers" surface (OPSFL-FLOWOPS-1 A2) over `/v1/flow-bindings`: which clock
 * types, signal types and API names start THIS flow's published version, the keys the engine has
 * seen fire UNBOUND (wireable in one click), and an "Add trigger" form. Wiring is hub config —
 * never part of the flow definition — so binding and unbinding never touch the artifact or its
 * publish state. Unbind keeps the row (disabled) for the record.
 */
export function TriggersDrawer(props: TriggersDrawerProps) {
  const { open, flowId, flowName, publishedVersion, onClose } = props;

  const [view, setView] = useState<FlowBindingsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<NormalizedApiError | null>(null);
  const [kind, setKind] = useState<FlowBindingKind>('CLOCK');
  const [typeKey, setTypeKey] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [actErr, setActErr] = useState<NormalizedApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      setView(await listFlowBindings());
    } catch (e) {
      setLoadErr(normalizeApiError(e));
      setView(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setKind('CLOCK');
    setTypeKey('');
    setActErr(null);
    setNotice(null);
    void load();
  }, [open, load]);

  const mine = useMemo(() => (view?.bindings ?? []).filter((b) => b.flowId === flowId), [view, flowId]);
  const enabledMine = mine.filter((b) => b.enabled);
  const disabledMine = mine.filter((b) => !b.enabled);
  const unbound = view?.unbound ?? [];
  const catalogue = view?.catalogue ?? [];
  const catalogueFor = (k: FlowBindingKind) => catalogue.filter((c) => c.kind === k);

  /** An enabled binding for the chosen key that points at ANOTHER flow (bind here replaces it). */
  const stealing = useMemo(
    () =>
      (view?.bindings ?? []).find(
        (b) => b.enabled && b.kind === kind && b.typeKey === typeKey.trim() && b.flowId !== flowId,
      ) ?? null,
    [view, kind, typeKey, flowId],
  );

  const keyProblem = (() => {
    const v = typeKey.trim();
    if (!v) return null;
    if (!TRIGGER_KEY_PATTERN.test(v)) {
      return 'Key must be an upper-snake name (A–Z, 0–9, _; starts with a letter; ≤ 64 chars).';
    }
    return null;
  })();
  const canBind = publishedVersion != null && typeKey.trim().length > 0 && !keyProblem && busy === null;

  const prefill = useCallback((k: FlowBindingKind, key: string) => {
    setKind(k);
    setTypeKey(key);
    setActErr(null);
    setNotice(null);
  }, []);

  const doBind = useCallback(async () => {
    if (!canBind || publishedVersion == null) return;
    setBusy('bind');
    setActErr(null);
    setNotice(null);
    try {
      const b = await bindTrigger({ kind, typeKey: typeKey.trim(), flowId, version: publishedVersion });
      setNotice(`${b.kind} ${b.typeKey} now starts ${b.flowId}@v${b.flowVersion}.`);
      setTypeKey('');
      await load();
    } catch (e) {
      setActErr(normalizeApiError(e));
    } finally {
      setBusy(null);
    }
  }, [canBind, publishedVersion, kind, typeKey, flowId, load]);

  const doUnbind = useCallback(
    async (b: FlowBindingView) => {
      setBusy(b.bindingId);
      setActErr(null);
      setNotice(null);
      try {
        await unbindTrigger(b.kind, b.typeKey);
        setNotice(`${b.kind} ${b.typeKey} unbound — its next fire is UNBOUND again (visible, never dropped). The row is kept.`);
        await load();
      } catch (e) {
        setActErr(normalizeApiError(e));
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  const errBanner = (err: NormalizedApiError | null, action: string) => {
    if (!err) return null;
    if (err.httpStatus === 403) {
      const key = action === 'load' ? 'flows.author' : 'flows.bindings.write';
      const roles = rolesFor(key);
      return (
        <Alert
          type="warning"
          showIcon
          message={action === 'load' ? 'You cannot read trigger bindings' : 'You cannot change trigger bindings'}
          description={
            <span>
              This needs <Text className="ol-mono">{key}</Text>
              {roles.length ? ` (${roles.join(', ')})` : ''}. Server said: {err.code} — {err.message}
            </span>
          }
        />
      );
    }
    if (err.code === 'FEATURE_DISABLED') {
      return (
        <Alert
          type="info"
          showIcon
          message="Trigger bindings are not enabled in this deployment"
          description={`${err.code} — ${err.message} (lifecycle-clocks is off; runs can still be started by hand or via POST /v1/runs).`}
        />
      );
    }
    if (isNotAvailable(err)) {
      return (
        <Alert
          type="info"
          showIcon
          message="Not available"
          description={`The trigger-binding endpoint is not reachable in this deployment (${err.code}).`}
        />
      );
    }
    return <Alert type="error" showIcon message={err.code} description={err.message} />;
  };

  const bindingRow = (b: FlowBindingView) => (
    <div
      key={b.bindingId}
      role="listitem"
      aria-label={`${b.kind} ${b.typeKey}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        border: '1px solid var(--ol-border)',
        borderRadius: 6,
        opacity: b.enabled ? 1 : 0.65,
      }}
    >
      <KindTag kind={b.kind} />
      <Text className="ol-mono" style={{ fontSize: 12, flex: 1 }}>
        {b.typeKey}
      </Text>
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        → v{b.flowVersion}
      </Text>
      {b.enabled ? (
        <Tag color="success" bordered={false} style={{ fontSize: 10.5 }}>
          wired
        </Tag>
      ) : (
        <Tag bordered={false} style={{ fontSize: 10.5 }}>
          unbound {b.updatedAt ? ts(b.updatedAt) : ''}
        </Tag>
      )}
      {b.enabled && (
        <Popconfirm
          title={`Unbind ${b.kind} ${b.typeKey}?`}
          description="The next fire of this key is recorded UNBOUND (visible, never dropped). The row is kept."
          okText="Unbind"
          onConfirm={() => void doUnbind(b)}
        >
          <Button size="small" danger type="text" loading={busy === b.bindingId} aria-label={`Unbind ${b.kind} ${b.typeKey}`}>
            Unbind
          </Button>
        </Popconfirm>
      )}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={
        <Space>
          <ApiOutlined />
          Triggers · {flowName ?? flowId}
        </Space>
      }
      styles={{ body: { padding: 16 } }}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
        Triggers are hub configuration, not part of the flow: a clock type, signal type or API name
        is wired to the exact published version it starts. Anything you leave unwired still fires —
        marked UNBOUND, visible below.
      </Paragraph>

      {errBanner(loadErr, 'load')}

      <Text strong style={{ fontSize: 12 }}>
        Wired to this flow
      </Text>
      <div role="list" aria-label="Triggers wired to this flow" style={{ display: 'grid', gap: 6, marginTop: 6 }}>
        {loading && !view ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Loading…
          </Text>
        ) : enabledMine.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {loadErr ? '—' : 'No trigger starts this flow yet. It can still be started by hand (Run) or via POST /v1/runs.'}
          </Text>
        ) : (
          enabledMine.map(bindingRow)
        )}
        {disabledMine.map(bindingRow)}
      </div>

      {unbound.length > 0 && (
        <>
          <Divider style={{ margin: '14px 0 8px' }} />
          <Text strong style={{ fontSize: 12 }}>
            Seen but unbound
          </Text>
          <Paragraph type="secondary" style={{ fontSize: 11.5, marginBottom: 6 }}>
            These keys fired in this hub with no flow wired. Wire one to this flow in one click.
          </Paragraph>
          <div role="list" aria-label="Seen but unbound" style={{ display: 'grid', gap: 6 }}>
            {unbound.map((u) => (
              <div
                key={`${u.kind}:${u.typeKey}`}
                role="listitem"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: '1px dashed var(--ol-border)', borderRadius: 6 }}
              >
                <KindTag kind={u.kind} />
                <Text className="ol-mono" style={{ fontSize: 12, flex: 1 }}>
                  {u.typeKey}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  seen {u.occurrences}× · last {ts(u.lastSeenAt)}
                </Text>
                <Button size="small" icon={<LinkOutlined />} onClick={() => prefill(u.kind, u.typeKey)} aria-label={`Wire ${u.kind} ${u.typeKey} to this flow`}>
                  Wire to this flow
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <Divider style={{ margin: '14px 0 8px' }} />
      <Text strong style={{ fontSize: 12 }}>
        Add trigger
      </Text>
      <Space direction="vertical" style={{ width: '100%', marginTop: 6 }} size="small">
        <Segmented
          options={(['CLOCK', 'SIGNAL', 'API'] as FlowBindingKind[]).map((k) => ({ label: k, value: k }))}
          value={kind}
          onChange={(v) => {
            setKind(v as FlowBindingKind);
            setTypeKey('');
            setActErr(null);
          }}
          aria-label="Trigger kind"
        />
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {KIND_HELP[kind]}
        </Text>
        {kind === 'API' ? (
          <Input
            value={typeKey}
            onChange={(e) => setTypeKey(e.target.value.toUpperCase())}
            placeholder="API name, e.g. ENTITY_EXIT"
            className="ol-mono"
            status={keyProblem ? 'error' : undefined}
            aria-label="Trigger key"
          />
        ) : (
          <Select
            showSearch
            style={{ width: '100%' }}
            value={typeKey || undefined}
            onChange={(v) => setTypeKey(v)}
            placeholder={catalogueFor(kind).length ? `${kind === 'CLOCK' ? 'Clock' : 'Signal'} type from the catalogue` : 'Catalogue unavailable — type a key'}
            options={catalogueFor(kind).map((c) => ({
              value: c.typeKey,
              label: `${c.typeKey}${c.status === 'RESERVED' ? ' (reserved)' : ''} — ${c.title}`,
            }))}
            aria-label="Trigger key"
            data-testid="trigger-key-select"
          />
        )}
        {keyProblem && (
          <Text type="danger" style={{ fontSize: 11.5 }}>
            {keyProblem}
          </Text>
        )}
        {stealing && (
          <Alert
            type="warning"
            showIcon
            message={`${stealing.kind} ${stealing.typeKey} currently starts ${stealing.flowId}@v${stealing.flowVersion}`}
            description="Binding it here replaces that wiring — one key starts exactly one flow version."
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Target:
          </Text>
          <Text className="ol-mono" style={{ fontSize: 12 }}>
            {flowId}
          </Text>
          {publishedVersion != null ? (
            <Tag color="success" bordered={false} style={{ fontSize: 10.5 }}>
              published v{publishedVersion}
            </Tag>
          ) : (
            <Tag color="warning" bordered={false} style={{ fontSize: 10.5 }}>
              nothing published
            </Tag>
          )}
        </div>
        {publishedVersion == null && (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Triggers bind the signed, published version only — publish this flow first.
          </Text>
        )}
        {errBanner(actErr, 'write')}
        {notice && <Alert type="success" showIcon message={notice} />}
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          disabled={!canBind}
          loading={busy === 'bind'}
          onClick={() => void doBind()}
        >
          Bind to v{publishedVersion ?? '—'}
        </Button>
      </Space>
    </Drawer>
  );
}
